import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from './Unicon';

const CameraFeed = ({ onCapture, onClose, facingMode = 'user' }) => {
    const { theme: { colors } } = useUnistyles();
    const [permission, requestPermission] = useCameraPermissions();
    const [facing, setFacing] = useState(facingMode === 'user' ? 'front' : 'back');
    const [cameraReady, setCameraReady] = useState(false);
    const [isFlashOn, setIsFlashOn] = useState(false);
    
    const cameraRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const isWeb = Platform.OS === 'web';

    useEffect(() => {
        if (isWeb) {
            startWebCamera();
        }

        return () => {
            if (isWeb && streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, [isWeb, facing]);

    const startWebCamera = async () => {
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }

            const constraints = {
                video: {
                    facingMode: facing === 'front' ? 'user' : 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                setCameraReady(true);
            }
        } catch (error) {
            console.error('Error accessing camera:', error);
            setCameraReady(false);
        }
    };

    const toggleCameraFacing = useCallback(() => {
        setFacing(current => (current === 'back' ? 'front' : 'back'));
    }, []);

    const toggleFlash = useCallback(() => {
        setIsFlashOn(prev => !prev);
    }, []);

    const takePicture = async () => {
        if (!cameraReady) return;

        try {
            if (isWeb) {
                const video = videoRef.current;
                const canvas = canvasRef.current;
                
                if (!video || !canvas) return;

                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                
                const ctx = canvas.getContext('2d');
                if (facing === 'front') {
                    ctx.translate(canvas.width, 0);
                    ctx.scale(-1, 1);
                }
                ctx.drawImage(video, 0, 0);

                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                }

                onCapture?.({ uri: dataUrl, type: 'image', width: canvas.width, height: canvas.height });
            } else {
                if (cameraRef.current) {
                    const photo = await cameraRef.current.takePictureAsync({
                        quality: 0.8,
                        base64: false,
                    });
                    onCapture?.(photo);
                }
            }
        } catch (error) {
            console.error('Error taking picture:', error);
        }
    };

    const handleClose = () => {
        if (isWeb && streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        onClose?.();
    };

    if (isWeb) {
        return (
            <View style={styles.container}>
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: facing === 'front' ? 'scaleX(-1)' : 'none'
                    }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                
                <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
                    <Unicon name="times" size={28} color={colors.onPrimary} />
                </TouchableOpacity>

                <View style={styles.controlsContainer}>
                    <TouchableOpacity style={styles.controlButton} onPress={toggleCameraFacing}>
                        <Unicon name="sync" size={28} color="white" />
                    </TouchableOpacity>
                    
                    <TouchableOpacity style={styles.captureButton} onPress={takePicture}>
                        <View style={styles.captureButtonInner} />
                    </TouchableOpacity>
                    
                    <View style={styles.controlButton} />
                </View>
            </View>
        );
    }

    if (!permission) {
        return (
            <View style={styles.container}>
                <Text style={styles.permissionText}>Requesting camera permission...</Text>
            </View>
        );
    }

    if (!permission.granted) {
        return (
            <View style={styles.container}>
                <Text style={styles.permissionText}>Camera permission is required</Text>
                <TouchableOpacity style={styles.permissionButton} onPress={requestPermission}>
                    <Text style={styles.permissionButtonText}>Grant Permission</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.closeButtonAlt} onPress={handleClose}>
                    <Text style={styles.permissionButtonText}>Cancel</Text>
                </TouchableOpacity>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <CameraView
                ref={cameraRef}
                style={styles.camera}
                facing={facing}
                flash={isFlashOn ? 'on' : 'off'}
                onCameraReady={() => setCameraReady(true)}
            />
            
            <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
                <Unicon name="times" size={28} color="white" />
            </TouchableOpacity>

            <View style={styles.controlsContainer}>
                <TouchableOpacity style={styles.controlButton} onPress={toggleFlash}>
                    <Unicon name={isFlashOn ? "bolt" : "bolt-slash"} size={28} color="white" />
                </TouchableOpacity>
                
                <TouchableOpacity style={styles.captureButton} onPress={takePicture}>
                    <View style={styles.captureButtonInner} />
                </TouchableOpacity>
                
                <TouchableOpacity style={styles.controlButton} onPress={toggleCameraFacing}>
                    <Unicon name="sync" size={28} color="white" />
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        backgroundColor: 'black',
        justifyContent: 'center',
        alignItems: 'center',
    },
    camera: {
        flex: 1,
        width: '100%',
    },
    closeButton: {
        position: 'absolute',
        top: 50,
        right: 20,
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
    },
    closeButtonAlt: {
        marginTop: 10,
        paddingHorizontal: 20,
        paddingVertical: 10,
        backgroundColor: colors.error,
        borderRadius: 8,
    },
    controlsContainer: {
        position: 'absolute',
        backgroundColor: 'rgba(0,0,0,0.5)',
        paddingBottom: 50,
        paddingTop: 20,
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
    },
    controlButton: {
        width: 50,
        height: 50,
        justifyContent: 'center',
        alignItems: 'center',
    },
    captureButton: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: 'white',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 4,
        borderColor: 'rgba(255,255,255,0.5)',
    },
    captureButtonInner: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: 'white',
    },
    permissionText: {
        color: 'white',
        fontSize: 16,
        fontFamily: fonts.regular,
        textAlign: 'center',
        marginBottom: 20,
    },
    permissionButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        backgroundColor: colors.primary,
        borderRadius: 8,
    },
    permissionButtonText: {
        color: 'white',
        fontSize: 16,
        fontFamily: fonts.medium,
    },
}));

export default CameraFeed;
